/**
 * Reconciliation Health Tile — Phase 2C Companion (Jun 2026)
 * ─────────────────────────────────────────────────────────────────────────
 * Read-only visualisation of claim-line reconciliation strategy counts.
 * Pulls from `GET /api/finance/reconciliation-health` which aggregates the
 * `audit_logs` collection. Renders nothing meaningful when the
 * `claim_line_id_reconcile_enabled` flag is OFF (the endpoint returns an
 * empty payload and we surface a tasteful 'dormant' state instead).
 *
 * Strict boundaries:
 *   • Read-only — never writes.
 *   • Reuses existing audit_logs collection.
 *   • Does not influence reconciliation behaviour.
 *   • Mounts only on the Finance dashboard.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Activity, CheckCircle2, AlertCircle, XCircle, RefreshCw, Info } from 'lucide-react';

const STRATEGY_META = {
  CLAIM_LINE_ID:        { label: 'Matched by claim_line_id (5-col CSV)',  tone: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2, dot: 'bg-emerald-500' },
  TIMESHEET_ID_FALLBACK:{ label: 'Fallback to timesheet_id (3-col CSV)',  tone: 'bg-amber-100 text-amber-800 border-amber-300',       icon: AlertCircle,  dot: 'bg-amber-500'   },
  NO_MATCH:             { label: 'No match found',                         tone: 'bg-red-100 text-red-800 border-red-300',             icon: XCircle,      dot: 'bg-red-500'     },
  MULTIPLE_MATCH:       { label: 'Ambiguous (multiple matches)',           tone: 'bg-rose-100 text-rose-800 border-rose-300',           icon: XCircle,      dot: 'bg-rose-500'    },
};

function StrategyRow({ k, count, percent }) {
  const meta = STRATEGY_META[k];
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-3 py-1.5" data-testid={`recon-strategy-row-${k}`}>
      <Icon className={`h-4 w-4 ${meta.tone.match(/text-(\S+)/)?.[0] || ''}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium truncate">{meta.label}</span>
          <span className="text-xs font-mono tabular-nums">
            <span data-testid={`recon-strategy-count-${k}`}>{count.toLocaleString()}</span>
            <span className="text-muted-foreground ml-1">({percent.toFixed(1)}%)</span>
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div className={`h-full ${meta.dot}`} style={{ width: `${percent}%` }} />
        </div>
      </div>
    </div>
  );
}

export function ReconciliationHealthTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHealth = async () => {
    const res = await api.get('/finance/reconciliation-health?days=30');
    return res.data?.data || null;
  };

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchHealth());
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetchHealth()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => {
        if (!cancelled) {
          setError(e.response?.data?.error?.message || e.message || 'Failed to load');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card data-testid="recon-health-tile-loading">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200" data-testid="recon-health-tile-error">
        <CardContent className="p-4 text-xs text-red-700">
          Reconciliation Health unavailable: {error}
        </CardContent>
      </Card>
    );
  }

  // Flag OFF state — tasteful dormant message
  if (!data?.flag_on) {
    return (
      <Card className="bg-muted/30 border-dashed" data-testid="recon-health-tile-dormant">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            Reconciliation Health
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-xs text-muted-foreground flex items-start gap-2">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>{data?.message || 'Phase 2C reconciliation is dormant. Enable claim_line_id_reconcile_enabled to start collecting strategy counts.'}</span>
          </p>
        </CardContent>
      </Card>
    );
  }

  const { totals, summary, latest_cycle } = data;
  const grand = summary?.grand_total_lines || 0;
  const pct = (n) => grand > 0 ? (n / grand) * 100 : 0;
  const adoption = summary?.new_format_adoption_percent || 0;
  const adoptionTone =
    adoption >= 80 ? 'text-emerald-600' :
    adoption >= 50 ? 'text-amber-600' :
    'text-rose-600';

  return (
    <Card data-testid="recon-health-tile">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              Reconciliation Health
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">last 30d</Badge>
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              How PRODA reconciliation responses are being matched to claim lines
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={refresh}
            data-testid="recon-health-refresh"
            className="h-7 w-7 p-0 -mr-1"
            aria-label="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Top KPIs */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-md bg-muted/50 p-2">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Cycles</p>
            <p className="text-lg font-semibold" data-testid="recon-cycles-count">
              {summary?.cycles_count ?? 0}
            </p>
          </div>
          <div className="rounded-md bg-muted/50 p-2">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Lines</p>
            <p className="text-lg font-semibold" data-testid="recon-grand-total">
              {grand.toLocaleString()}
            </p>
          </div>
          <div className="rounded-md bg-muted/50 p-2">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">5-col adoption</p>
            <p className={`text-lg font-semibold ${adoptionTone}`} data-testid="recon-adoption-percent">
              {adoption.toFixed(0)}%
            </p>
          </div>
        </div>

        {/* Strategy breakdown */}
        {grand > 0 ? (
          <div className="space-y-1">
            <StrategyRow k="CLAIM_LINE_ID"         count={totals.CLAIM_LINE_ID}         percent={pct(totals.CLAIM_LINE_ID)} />
            <StrategyRow k="TIMESHEET_ID_FALLBACK" count={totals.TIMESHEET_ID_FALLBACK} percent={pct(totals.TIMESHEET_ID_FALLBACK)} />
            <StrategyRow k="NO_MATCH"              count={totals.NO_MATCH}              percent={pct(totals.NO_MATCH)} />
            <StrategyRow k="MULTIPLE_MATCH"        count={totals.MULTIPLE_MATCH}        percent={pct(totals.MULTIPLE_MATCH)} />
          </div>
        ) : (
          <div className="rounded-md border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground flex items-center gap-2" data-testid="recon-empty-state">
            <Info className="h-3.5 w-3.5 shrink-0" />
            <span>No reconciliations in the last 30 days. Tile will populate once Finance runs the first cycle.</span>
          </div>
        )}

        {/* Latest cycle */}
        {latest_cycle && (
          <div className="rounded-md bg-muted/30 border p-2 text-[11px] space-y-0.5" data-testid="recon-latest-cycle">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">Latest cycle</span>
              <span className="text-muted-foreground">
                {latest_cycle.timestamp ? new Date(latest_cycle.timestamp).toLocaleString() : '—'}
              </span>
            </div>
            <div className="text-muted-foreground">
              Claim <span className="font-mono">{(latest_cycle.claim_id || '').slice(0, 8)}…</span>
              {' · '}{latest_cycle.updates_count ?? 0} lines
              {latest_cycle.batch_status && ` · batch ${latest_cycle.batch_status}`}
            </div>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground">
          Read-only · derived from <code className="bg-muted px-1 rounded">audit_logs</code> · does not affect reconciliation behaviour.
        </p>
      </CardContent>
    </Card>
  );
}

export default ReconciliationHealthTile;
